<template>
  <div v-if="featureEnabled" class="jcp" ref="wrapper">
    <!-- Picked state: collapsed field with clear × -->
    <div v-if="modelValue && !searchQuery" class="jcp__picked">
      <JobCategoryIcon size="normal" class="jcp__picked-icon" />
      <span class="jcp__name">{{ pickedCategory?.name }}</span>
      <span v-if="pickedCategory?.totalScore != null" class="jcp__score-pill">{{ pickedCategory.totalScore }}</span>
      <span v-if="pickedCategory?.code" class="jcp__code">#{{ pickedCategory.code }}</span>
      <div
        class="jcp__clear"
        role="button"
        tabindex="0"
        aria-label="Clear selection"
        @click.stop="handleClear"
        @keydown.enter.stop="handleClear"
        @keydown.space.stop.prevent="handleClear"
      >
        <Icon name="x_close_icon" />
      </div>
    </div>

    <!-- Search input (shown in all non-picked states) -->
    <div v-else class="jcp__search-wrapper" ref="searchWrapper">
      <ExTextInput
        :value="searchQuery"
        :ghostText="placeholder"
        :aria-label="placeholder"
        icon="interface_search"
        :hideFloatingLabel="true"
        @input="onInput"
        ref="searchInputRef"
      />

      <!-- Results dropdown. Portaled to document.body when inlineResults is false. -->
      <div
        v-if="showDropdown"
        ref="dropdown"
        class="jcp__dropdown"
        :class="{ 'jcp__dropdown--inline': inlineResults }"
        role="listbox"
        :style="inlineResults ? undefined : dropdownStyle"
        :data-jcp-dropdown="portalId"
      >
        <!-- Loading state: 3 skeleton rows -->
        <div v-if="loading" class="jcp__skeleton-list">
          <div
            v-for="i in 3"
            :key="i"
            class="jcp__skeleton-row"
          >
            <div class="jcp__skeleton-bar" :style="{ width: skeletonWidths[i - 1] }"></div>
          </div>
        </div>

        <!-- Results -->
        <template v-else>
          <div v-if="sortedResults.length > 0" class="jcp__results-list" :style="resultsListStyle">
            <template
              v-for="row in sortedResults"
            >
              <!-- Disabled row (Draft or Retired) with tooltip -->
              <EXTooltip
                v-if="!isActive(row)"
                :key="`disabled-${row.jobCategoryId}`"
                :text="tooltipText(row)"
                :disabled="false"
              >
                <div
                  role="option"
                  tabindex="-1"
                  :aria-disabled="true"
                  class="jcp__row jcp__row--disabled"
                >
                  <JobCategoryIcon size="normal" class="jcp__row-icon jcp__row-icon--disabled" />
                  <span class="jcp__row-name jcp__row-name--disabled">{{ row.name }}</span>
                  <OrgObjectStatusBadge
                    :status="row.status"
                    :disabled="true"
                    class="jcp__row-badge"
                  />
                  <span v-if="row.totalScore != null" class="jcp__score-pill jcp__score-pill--disabled">{{ row.totalScore }}</span>
                  <span class="jcp__row-code jcp__row-code--disabled">#{{ row.code }}</span>
                </div>
              </EXTooltip>

              <!-- Active row (clickable) -->
              <div
                v-else
                :key="`active-${row.jobCategoryId}`"
                role="option"
                tabindex="0"
                class="jcp__row"
                @mousedown.prevent
                @click.stop="selectRow(row)"
                @keydown.enter="selectRow(row)"
                @keydown.space.prevent="selectRow(row)"
              >
                <JobCategoryIcon size="normal" class="jcp__row-icon" />
                <span class="jcp__row-name">{{ row.name }}</span>
                <OrgObjectStatusBadge
                  :status="row.status"
                  class="jcp__row-badge"
                />
                <span v-if="row.totalScore != null" class="jcp__score-pill">{{ row.totalScore }}</span>
                <span class="jcp__row-code">#{{ row.code }}</span>
              </div>
            </template>
          </div>

          <!-- No results -->
          <div v-else class="jcp__no-results">
            <span class="jcp__no-results-text">No job categories found</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted, nextTick } from "vue";
import Icon from "@components/icons/Icon.vue";
import JobCategoryIcon from "@components/icons/items/OrgObject/JobCategoryIcon.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { LD_FLAG_IDEA_MDP_566 } from "@components/constants/launchDarklyFlagKeys";

/**
 * JobCategoryPicker — ER-12040. Single-step picker that selects one Active Job Category
 * for a Position. Lists categories on open, searches by name or code with exact match
 * pinned first, shows status badge, Total Score pill and code per row. Draft and Retired
 * rows are shown disabled with tooltip. No inline "+ Add Job Category" row.
 *
 * Embedded by JobCategoryPickerDialog (ER-12041). Copied from JobFamilyPickerWithAdd
 * (ER-11726) minus the Level step and "+ Add" row. Behind the idea-MDP-566 kill switch
 * shared with the rest of Job Categories.
 */
const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  results: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  visibleRowCount: {
    type: Number,
    default: 3,
  },
  placeholder: {
    type: String,
    default: "Search job categories by name or code",
  },
  /** Keep the results list in the dialog/card DOM (no body portal). Used by JobCategoryPickerDialog. */
  inlineResults: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "search"]);

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const featureEnabled = ref(true);
const portalId = `jcp-dropdown-${instance?._uid ?? instance?.uid ?? Date.now()}`;

const searchQuery = ref("");
const wrapper = ref(null);
const searchInputRef = ref(null);
const searchWrapper = ref(null);
const dropdown = ref(null);
const dropdownStyle = ref({});
let searchDebounceTimeout = null;

const skeletonWidths = ["62%", "55%", "78%"];

const pickedCategory = computed(() => {
  if (!props.modelValue?.jobCategoryId) return null;
  return props.results.find((cat) => cat.jobCategoryId === props.modelValue.jobCategoryId);
});

const showDropdown = computed(() => !props.modelValue);

const sortedResults = computed(() => {
  if (!Array.isArray(props.results)) return [];
  const query = searchQuery.value.trim().toLowerCase();
  const withExactMatch = [];
  const withoutExactMatch = [];

  for (const category of props.results) {
    const nameMatch = category.name?.toLowerCase() === query;
    if (nameMatch) {
      withExactMatch.push(category);
    } else {
      withoutExactMatch.push(category);
    }
  }

  withoutExactMatch.sort((a, b) => {
    const nameA = a.name?.toLowerCase() || "";
    const nameB = b.name?.toLowerCase() || "";
    return nameA.localeCompare(nameB);
  });

  return [...withExactMatch, ...withoutExactMatch];
});

const resultsListStyle = computed(() => {
  const rowHeight = 40;
  const gap = 4;
  const maxHeight = props.visibleRowCount * rowHeight + (props.visibleRowCount - 1) * gap;
  return {
    maxHeight: `${maxHeight}px`,
  };
});

function isActive(category) {
  return category.status?.toLowerCase() === "active";
}

function tooltipText(category) {
  return "Only Active job categories can be assigned";
}

function onInput(value) {
  searchQuery.value = value;
  clearTimeout(searchDebounceTimeout);
  searchDebounceTimeout = setTimeout(() => {
    emit("search", searchQuery.value.trim());
  }, 300);
}

function selectRow(row) {
  if (!isActive(row)) return;
  searchQuery.value = "";
  emit("update:modelValue", {
    jobCategoryId: row.jobCategoryId,
  });
}

function handleClear() {
  emit("update:modelValue", null);
}

function handleOutsideClick(e) {
  const insideWrapper = wrapper.value && wrapper.value.contains(e.target);
  const insideDropdown = dropdown.value && dropdown.value.contains(e.target);
  if (insideWrapper || insideDropdown) return;
  if (searchQuery.value && !props.modelValue) {
    searchQuery.value = "";
    emit("search", "");
  }
}

/* ---- Portaled dropdown (Vue 2 has no Teleport) --------------------------- */
function attachDropdownToBody() {
  const el = dropdown.value;
  if (el && el.parentNode !== document.body) {
    document.body.appendChild(el);
  }
}

function removePortaledDropdownFromDom() {
  const el = dropdown.value;
  if (el?.parentNode) {
    el.parentNode.removeChild(el);
    return;
  }
  document.querySelector(`[data-jcp-dropdown="${portalId}"]`)?.remove();
}

function updateDropdownPosition() {
  const anchor = searchWrapper.value;
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  const menuHeight = dropdown.value?.offsetHeight ?? 0;
  const gap = 8;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = menuHeight > 0 && spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;
  const top = openAbove ? rect.top - menuHeight - gap : rect.bottom + gap;

  dropdownStyle.value = {
    position: "fixed",
    top: `${Math.round(top)}px`,
    left: `${Math.round(rect.left)}px`,
    width: `${Math.round(rect.width)}px`,
    // Above JobCategoryPickerDialog overlay--stacked (10002)
    zIndex: 10003,
  };
}

function syncOpenDropdown() {
  nextTick(() => {
    attachDropdownToBody();
    updateDropdownPosition();
    nextTick(updateDropdownPosition);
  });
}

function openDropdown() {
  syncOpenDropdown();
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
}

function closeDropdown() {
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
  nextTick(removePortaledDropdownFromDom);
}

watch(showDropdown, (open) => {
  if (props.inlineResults) return;
  if (open) {
    openDropdown();
  } else {
    closeDropdown();
  }
});

onMounted(async () => {
  document.addEventListener("click", handleOutsideClick);
  if (showDropdown.value) {
    if (!props.inlineResults) {
      openDropdown();
    }
    // Emit search("") on mount so list shows immediately
    emit("search", "");
  }
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
  if (!props.inlineResults) {
    window.removeEventListener("scroll", updateDropdownPosition, true);
    window.removeEventListener("resize", updateDropdownPosition);
    removePortaledDropdownFromDom();
  }
  clearTimeout(searchDebounceTimeout);
});
</script>

<style scoped>
.jcp {
  width: 100%;
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.jcp__search-wrapper {
  position: relative;
  width: 100%;
}

.jcp__dropdown {
  font-family: Inter, sans-serif;
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 10;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
}

.jcp__dropdown--inline {
  position: static;
  margin-top: 8px;
  box-shadow: none;
}

.jcp__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
  min-height: 44px;
}

.jcp__picked-icon {
  flex-shrink: 0;
}

.jcp__name {
  flex: 1;
  min-width: 0;
  color: #344054;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.jcp__score-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: transparent;
  border: 1.5px solid #475467;
  border-radius: 9999px;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  flex-shrink: 0;
}

.jcp__score-pill--disabled {
  border-color: #d0d5dd;
  color: #d0d5dd;
}

.jcp__code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #475467;
  line-height: 20px;
  white-space: nowrap;
}

.jcp__clear {
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #98a2b3;
  padding: 2px;
  border-radius: 4px;
}

.jcp__clear:hover {
  color: #667085;
  background: #f2f4f7;
}

.jcp__skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.jcp__skeleton-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  height: 40px;
  box-sizing: border-box;
}

.jcp__skeleton-bar {
  height: 16px;
  background: #eaecf0;
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.jcp__results-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.jcp__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #f2f4f7;
  border-radius: 10px;
  height: 40px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.jcp__row:hover {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.jcp__row:focus {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.jcp__row--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.jcp__row--disabled:hover {
  background: #ffffff;
  border-color: #f2f4f7;
}

.jcp__row-icon {
  flex-shrink: 0;
}

.jcp__row-icon--disabled {
  opacity: 0.4;
}

.jcp__row-name {
  flex: 1;
  min-width: 0;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.jcp__row-name--disabled {
  color: #d0d5dd;
}

.jcp__row-badge {
  flex-shrink: 0;
}

.jcp__row-code {
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #475467;
  line-height: 20px;
  white-space: nowrap;
  margin-left: auto;
}

.jcp__row-code--disabled {
  color: #d0d5dd;
}

.jcp__no-results {
  padding: 20px;
  text-align: center;
}

.jcp__no-results-text {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
</style>
